// AdminPort: everything the admin console (design admin-taskcoolV1) needs from the outside world. Two adapters:
//  - httpAdminPort (real): the /api/v1/admin JSON API, session cookie + CSRF (frontend/src/api/client.ts);
//  - demoAdmin (dev / ?demo only, frontend/src/dev/demoAdmin.ts): the reference prototype's sample data in API
//    shapes, in memory, deterministic, for visual parity. Never used for real tenants.
// Shapes mirror the backend contract (docs/api/openapi.yaml, Administration). The console never requests task
// titles or notes; names of private projects arrive as null with `hidden: true`. `null` numbers = not measured.

/** Server staff roles (src/Modules/Administration/Domain/ValueObjects/StaffRole.php). */
export type StaffRole = 'superadmin' | 'admin' | 'support' | 'finance' | 'viewer';
export type Range = '7d' | '30d' | '90d' | '1y';
export type AuditType = 'auth' | 'user' | 'project' | 'share' | 'setting';

export interface AdminMe { role: StaffRole; permissions: string[]; brand: { name: string }; two_factor?: { required: boolean; verified: boolean }; user?: { id: string; name: string; email: string; color: string; photo_url?: string | null } }

export interface AuditEntry {
  id: string; at: string; actor_name: string | null; actor_color?: string | null; action: string; target: string;
  workspace_name: string | null; type?: AuditType | null; ip?: string | null;
}
export interface AuditPage { entries: AuditEntry[]; next_cursor: string | null }

export interface Pair { current: number; previous: number }
export interface AdminOverview {
  range: Range;
  users_total: number;
  signups: Pair; active_users: Pair; tasks_created: Pair; tasks_done: Pair;
  buckets: Array<{ start: string; created: number; done: number }>;
  top_projects: Array<{ id: string; name: string | null; hidden: boolean; total_tasks: number; color: string | null }>;
  recent: AuditEntry[];
  health: { email_queue: number | null; uptime_30d: number | null; api_latency_ms: number | null; error_rate_24h: number | null };
  storage?: { used_bytes: number; disk_total_bytes: number | null; disk_free_bytes: number | null };
}

export interface AdminUser {
  id: string; name: string; email: string; color: string; status: 'active' | 'disabled';
  created_at: string; last_login_at: string | null; workspaces: number; staff_role: StaffRole | null;
  projects?: number; tasks?: number; last_active_at?: string | null; storage_bytes?: number | null;
  /** Same-origin admin photo URL (`/api/v1/admin/users/{id}/avatar?v=…`); null without a profile photo. */
  photo_url?: string | null;
}
export interface UsersPage { users: AdminUser[]; next_cursor: string | null }
export interface UserDetail {
  user: AdminUser;
  projects: Array<{ id: string; name: string | null; hidden: boolean; role: string; color: string | null }>;
  sessions: Array<{ device: string | null; ip: string | null; last_seen_at: string | null; created_at: string }>;
}
/** TaskCool 9.4 (design Admin Panel "Usage"): [created, completed, other] actions of one local day. */
export type UsageDay = [number, number, number];
/** `series`: the range's buckets (1d hourly, 3d 4-hourly, 7d 6-hourly, 30d daily); `days`: the range's local days. */
export interface UsageRow { user_id: string; name: string; color: string; photo_url?: string | null; series: UsageDay[]; days: UsageDay[] }
export type UsageRange = 1 | 3 | 7 | 30;
/** Every person of the brand, most active first; buckets oldest first. */
export interface UsageSummary { range: UsageRange; from: string; bucket_seconds: number; days_from: string; users: UsageRow[] }
export interface UserUsage {
  user: AdminUser; range: UsageRange; from: string; bucket_seconds: number; days_from: string; series: UsageDay[]; days: UsageDay[];
  /** all time */
  total: number; created: number; completed: number; first_at: string | null; last_at: string | null;
  /** heat[dow][hour], dow 0 = Sunday, in the viewer's time zone */
  heat: number[][];
  projects: Array<{ id: string; name: string; count: number }>;
  recent: Array<{ kind: string; at: string; task: string | null; project: string | null }>;
}
export interface UserInvitation { id: string; email: string; role: 'member' | 'support' | 'admin'; created_at: string; invited_by: string | null }

export interface Person { name: string; color: string | null }
export interface AdminProject {
  id: string; workspace_id: string; workspace_name: string;
  /** null when `hidden`: a private project whose name/content staff may not see. */
  name: string | null; hidden: boolean; visibility: string; members: number;
  tasks: { open: number; done: number }; created_at: string; owner_name: string;
  owner?: Person; members_preview?: Person[]; open?: number; total?: number; link_active?: boolean;
  storage_bytes?: number | null; updated_at?: string | null; color?: string | null;
}

export interface AdminLink {
  id: string; project_id: string; project_name: string | null; hidden: boolean; scope: 'project' | 'list' | 'tag';
  list_name: string | null; tag: string | null; by_name: string | null; created_at: string; last_viewed_at: string | null; active: boolean; url: string; views?: number;
}
export interface AdminInvitation {
  id: string; email: string; project_name: string | null; role: string; status: string;
  created_at: string; expires_at: string; invited_by: string;
}

export interface TrashItem {
  id: string; kind: 'project' | 'list' | 'task' | 'file'; name: string | null; hidden: boolean; project_name: string | null;
  deleted_by_name: string | null; deleted_at: string; task_count: number | null;
}
export interface TrashPage { retention_days: number; items: TrashItem[] }

export interface AdminSettings {
  display_name: string; short_name: string; accent: string; theme: string; lang: string; signups: boolean;
  support_email: string | null; support_url: string | null; terms_url: string | null; privacy_url: string | null;
  google_signin_enabled?: boolean; max_upload_bytes?: number; trash_retention_days?: number;
  // 8.6 (docs/admin-v2-contract.md)
  signup_mode?: 'open' | 'invite' | 'closed'; google_signin_allowed?: boolean; admin_2fa?: boolean; maintenance?: boolean;
  default_calendar?: 'jalali' | 'gregorian'; max_upload_mb?: number; max_upload_hard_mb?: number;
  role_permissions?: { admin: string[]; support: string[] };
  notifications?: NotifSettings; email_look?: { logo: boolean; rtl: boolean; unsubscribe: boolean };
  templates?: Record<string, [string, string]>;
}
export type NotifEvent = 'mention' | 'invite' | 'deadline' | 'newdevice' | 'digest' | 'otp';
export interface NotifSettings {
  channels: { app: boolean; email: boolean };
  events: Partial<Record<NotifEvent, { app: boolean; email: boolean }>>;
  available?: Partial<Record<NotifEvent, Array<'app' | 'email'>>>;
  /** null = never configured: legacy reminder at 09:00 on the due day. */
  lead: '1h' | '3h' | '1d' | '2d' | null; digest_day: 'sat' | 'wed' | 'off';
}
export interface ProjectMember { user_id: string; name: string; color: string | null; photo_url?: string | null; role: string }

export interface MailStatus {
  configured: boolean; transport: string | null; host: string | null; port: number | string | null; encryption: string | null;
  from_name: string | null; from_address: string | null; reply_to: string | null;
}
export interface NotifSummary { today: { in_app: number; email_sent: number | null }; email_success_rate_30d: number | null }

/** ADR 0017 platform backups (permission `manageBackups`). Instants are UTC ISO 8601. */
export type BackupKind = 'scheduled' | 'manual' | 'pre_restore';
export type BackupOffsite = 'pending' | 'uploaded' | 'failed' | 'disabled';
export interface Backup {
  id: string; kind: BackupKind; status: 'running' | 'ok' | 'failed'; created_at: string; finished_at: string | null;
  size_bytes: number | null; counts: { users?: number; tasks?: number; files?: number; [k: string]: number | undefined } | null;
  schema: string | number | null; local: boolean; offsite: BackupOffsite; error: string | null;
  /** exact text to type for a restore (ADR 0017) */
  restore_confirmation?: string;
}
export type BackupJobStatus = 'queued' | 'running' | 'done' | 'failed';
export interface BackupJob {
  id: string; type: 'backup' | 'restore'; status: BackupJobStatus; step: string | null; backup_id: string | null;
  result_backup_id: string | null; requested_by: string | null; requested_at: string; started_at: string | null;
  finished_at: string | null; error: string | null;
}
/** When scheduled backups run: every `every_hours` hours from `at` (HH:MM, Asia/Tehran). */
export interface BackupSchedule { every_hours: number; at: string; timezone: string }
export interface BackupsOverview {
  config: { encryption: boolean; offsite: boolean; schedule: BackupSchedule | string; keep: { hourly: number; daily: number; monthly: number; local: number } };
  status: { last_success_at: string | null; last_failure_at: string | null; next_at: string | null; stale: boolean; agent_seen_at?: string | null };
  jobs: BackupJob[]; backups: Backup[];
}

/** Failure categories surfaced by adapters (mapped from HTTP status). */
export class AdminError extends Error {
  constructor(public kind: 'auth' | 'forbidden' | 'network' | 'rejected' | 'server', message: string, public code = '') { super(message); }
}

export interface AdminPort {
  /** true only for the in-memory demo adapter */
  readonly demo: boolean;
  me(): Promise<AdminMe>;
  overview(range: Range): Promise<AdminOverview>;
  users(q?: { q?: string; limit?: number; cursor?: string | null }): Promise<UsersPage>;
  user(id: string): Promise<UserDetail>;
  /** `tz` = the viewer's UTC offset in minutes (TaskCool 9.4). */
  usage(range: UsageRange, tz: number): Promise<UsageSummary>;
  userUsage(id: string, range: UsageRange, tz: number): Promise<UserUsage>;
  setUserStatus(id: string, status: 'active' | 'disabled'): Promise<void>;
  signOutUser(id: string): Promise<void>;
  setStaffRole(userId: string, role: StaffRole): Promise<void>;
  removeStaff(userId: string): Promise<void>;
  userInvitations(): Promise<{ invitations: UserInvitation[] }>;
  inviteUser(email: string, role: 'member' | 'support' | 'admin'): Promise<{ status?: string }>;
  cancelUserInvitation(id: string): Promise<void>;
  projects(): Promise<{ projects: AdminProject[] }>;
  links(): Promise<{ links: AdminLink[] }>;
  setLinkActive(id: string, active: boolean): Promise<void>;
  invitations(): Promise<{ invitations: AdminInvitation[] }>;
  revokeInvitation(id: string): Promise<void>;
  audit(q?: { type?: string; limit?: number; cursor?: string | null }): Promise<AuditPage>;
  releases(): Promise<{ hidden_builtin: string[] }>;
  setReleaseHidden(version: string, hidden: boolean): Promise<void>;
  trash(): Promise<TrashPage>;
  settings(): Promise<AdminSettings>;
  saveSettings(patch: Partial<AdminSettings>): Promise<AdminSettings>;
  mail(): Promise<MailStatus>;
  testMail(): Promise<{ status: 'sent' | 'queued' | 'failed'; error?: string }>;
  notifSummary(): Promise<NotifSummary>;
  deleteUser(id: string): Promise<void>;
  restoreUser(id: string): Promise<void>;
  sendPasswordReset(id: string): Promise<void>;
  emailUsers(userIds: string[], subject: string, body: string): Promise<{ queued: number }>;
  projectMembers(id: string): Promise<{ members: ProjectMember[] }>;
  transferProject(id: string, userId: string): Promise<void>;
  trashProject(id: string): Promise<void>;
  restoreTrash(kind: string, id: string): Promise<void>;
  purgeTrash(kind: string, id: string): Promise<void>;
  emptyTrash(): Promise<{ purged: number; remaining: boolean } | void>;
  resendInvitation(id: string): Promise<void>;
  send2fa(): Promise<void>;
  verify2fa(code: string): Promise<void>;
  /** Same-origin URL of the audit CSV export (downloaded by the browser, never fetched into the app). */
  exportAuditUrl(type: string): string;
  // ADR 0017 backups. Optional: the in-memory demo adapter does not offer them (the section then says so).
  backups?(): Promise<BackupsOverview>;
  backupNow?(): Promise<{ job: BackupJob }>;
  backupJob?(id: string): Promise<{ job: BackupJob }>;
  backupSchedule?(everyHours: number, at: string): Promise<{ schedule: BackupSchedule }>;
  /** One-time link (5 min) to the decrypted archive; the browser downloads it. */
  backupDownload?(id: string, password: string): Promise<{ url: string; expires_at: string }>;
  /** `confirm` = `RESTORE <created_at>` exactly. */
  backupRestore?(id: string, password: string, confirm: string): Promise<{ job: BackupJob }>;
}
